import { Flex, Tag, Typography } from 'antd';
import { IconCalendar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { LandlordPartnerRowDto } from '@evimo/client/models';
import {
  SlaExpandedDetailGrid,
  type SlaExpandedDetailItem,
} from '~/app/landing/sla/shared/components/SlaExpandedDetailGrid';
import { SlaCurrencyLabel } from '~/app/landing/sla/shared/components/SlaCurrencyLabel';
import {
  formatCurrency,
  formatDateLabel,
} from '~/app/landing/sla/shared/utils/sla-format.utils';

type Props = {
  row: LandlordPartnerRowDto;
};

export function LandlordPartnerExpandedRow({ row }: Props) {
  const { t } = useTranslation('sla');
  const contract = row.slaContract;

  if (!contract) {
    return (
      <Typography.Text type="secondary">
        {t('table.noContract')}
      </Typography.Text>
    );
  }

  const items: SlaExpandedDetailItem[] = [
    {
      key: 'paymentModel',
      label: t('landlords.fields.paymentModel'),
      value: t(`paymentModel.${contract.paymentModel}`, contract.paymentModel),
    },
    {
      key: 'penaltyCurrency',
      label: t('landlords.fields.penaltyCurrency'),
      value: <SlaCurrencyLabel code={contract.penaltyCurrency} />,
    },
    {
      key: 'penaltyAmount',
      label: t('landlords.fields.penalty'),
      value: `${formatCurrency(
        contract.penaltyPerDayAmount,
        contract.penaltyCurrency,
      )}/d`,
    },
    {
      key: 'validFrom',
      label: t('landlords.fields.validFrom'),
      value: formatDateLabel(contract.validFrom),
    },
    {
      key: 'validTo',
      label: t('landlords.fields.validTo'),
      value: contract.validTo
        ? formatDateLabel(contract.validTo)
        : t('review.openEnded'),
    },
  ];

  return (
    <div className="sla-table-expanded-panel">
      <Flex align="center" gap={8} className="sla-table-expanded-panel__title">
        <IconCalendar size={16} />
        <Typography.Text strong>{t('table.contractDetails')}</Typography.Text>
        {row.isSameCompanyAsOperator ? (
          <Tag style={{ borderRadius: 9999, margin: 0 }}>
            {t('landlords.sameCompany')}
          </Tag>
        ) : null}
      </Flex>
      <SlaExpandedDetailGrid items={items} />
    </div>
  );
}
